/**
 * Analysis Engine — orchestrates all intelligence analysis modules
 * on a 60-second cycle with 15-minute CII warmup.
 *
 * Phase 1 modules:
 * - Focal Point Detection (entity-level multi-source attention)
 * - Surge Detection (volume anomalies against rolling baseline)
 * - Geographic Convergence (3+ event types in 1-degree grid cells)
 * - Country Intelligence Index (composite risk for 20 priority countries)
 * - Cross-Source Correlation (temporal + spatial proximity across sources)
 *
 * Phase 2 modules:
 * - Entity Extraction (batch, on new events)
 * - Hotspot Escalation (needs focal points + CII + convergence)
 * - Infrastructure Cascade (needs events with infrastructure tags)
 * - Signal Aggregation (needs all other results)
 * - Cross-Module Composite Risk (needs everything above)
 * - Velocity Tracking (lightweight, every tick)
 * - Trending Keywords (lightweight, every tick)
 * - Temporal Baseline (lightweight, every tick)
 *
 * Emits:
 * - analysis:complete (combined AnalysisResult)
 * - analysis:focal-point (per focal point)
 * - analysis:surge (per surge)
 * - analysis:convergence (per convergence cell)
 * - analysis:hotspot (per hotspot escalation)
 * - analysis:cascade (per cascade result)
 * - analysis:risk (per composite risk)
 * - analysis:velocity (velocity metrics)
 * - analysis:trending (trending keywords)
 * - analysis:signal (per aggregated signal)
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { saveAnalysisResult } from '@/storage/indexeddb';
import type { AnalysisResult, CIIScore } from './types';
import { detectFocalPoints } from './focal-point';
import { detectSurges, EventHistory } from './surge-detect';
import { detectConvergence } from './convergence';
import { calculateAllCII } from './cii';
import { detectCorrelations } from './correlation';
import { detectHotspotEscalations } from './hotspot-escalation';
import { analyzeCascade, findAffectedNodes } from './infrastructure-cascade';
import { aggregateSignals } from './signal-aggregator';
import { calculateCompositeRisk } from './cross-module';
import { calculateVelocity } from './velocity';
import { detectTrendingKeywords } from './trending-keywords';
import { updateBaselines } from './temporal-baseline';
import { detectBreakingNews } from './breaking-news';
import { analyzeSentiment } from './sentiment';
import { detectAISSpoofing } from './ais-spoofing';

const ANALYSIS_INTERVAL_MS = 60_000; // 60 seconds
const CII_WARMUP_MS = 30 * 1000; // 30 seconds (fast startup, baseline improves over time)

export class AnalysisEngine {
  private timer: ReturnType<typeof setInterval> | null = null;
  private running = false;
  private bootTime = 0;
  private eventHistory: EventHistory;
  private historySeeded = false;
  private previousCII: Map<string, CIIScore> = new Map();
  private lastResult: AnalysisResult | null = null;

  constructor() {
    this.eventHistory = new EventHistory();
  }

  /** Start the analysis cycle. Runs initial tick after 5s, then every 60s. */
  start(): void {
    if (this.timer) return;

    this.bootTime = Date.now();

    // Run first tick after short delay (let initial data sources load)
    setTimeout(() => {
      this.tick().catch((err) => {
        console.error('[Sentinel] Analysis engine initial tick error:', err);
      });
    }, 5_000);

    this.timer = setInterval(() => {
      this.tick().catch((err) => {
        console.error('[Sentinel] Analysis engine error:', err);
      });
    }, ANALYSIS_INTERVAL_MS);

    console.log(
      '[Sentinel] Analysis engine started (5s initial, 60s cycle, 15m CII warmup)',
    );
  }

  /** Stop the analysis cycle. */
  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.running = false;
    console.log('[Sentinel] Analysis engine stopped');
  }

  /** Get last analysis result (may be null before first cycle completes). */
  getLastResult(): AnalysisResult | null {
    return this.lastResult;
  }

  /** Check whether CII warmup period has elapsed. */
  isCIIActive(): boolean {
    return Date.now() - this.bootTime >= CII_WARMUP_MS;
  }

  /** Single analysis tick — runs all modules, emits results via event bus. */
  private async tick(): Promise<void> {
    // Debounce: skip if a previous tick is still running
    if (this.running) return;
    this.running = true;

    try {
      const events = appStore.getState().events;
      if (events.length === 0) return;

      const ciiActive = this.isCIIActive();

      // ---------------------------------------------------------------
      // Phase 1: Core analysis modules
      // ---------------------------------------------------------------
      // Pre-seed baseline history from existing events on first tick so surge
      // detection has enough windows immediately (events carry real timestamps
      // spanning hours/days from initial data loads).
      if (!this.historySeeded) {
        this.historySeeded = true;
        this.eventHistory.recordEvents(events);
      }

      const focalPoints = detectFocalPoints(events, appStore.getState().timeRange);
      const surges = detectSurges(events, this.eventHistory);
      const convergences = detectConvergence(events);
      const correlations = detectCorrelations(events);

      // CII only after 15-minute warmup period
      let ciiScores: Map<string, CIIScore>;
      if (ciiActive) {
        ciiScores = calculateAllCII(events, this.previousCII);
        this.previousCII = ciiScores;
      } else {
        ciiScores = new Map();
      }

      // ---------------------------------------------------------------
      // Phase 2: Advanced analysis modules
      // ---------------------------------------------------------------

      // Build intermediate result for modules that depend on Phase 1 output
      const intermediateResult: AnalysisResult = {
        focalPoints,
        surges,
        convergences,
        ciiScores,
        correlations,
        hotspotEscalations: [],
        cascades: [],
        compositeRisks: [],
        aggregatedSignals: [],
        velocity: null,
        trendingKeywords: [],
        breakingNews: [],
        sentimentScores: [],
        aisAnomalies: [],
        timestamp: new Date().toISOString(),
      };

      // Hotspot escalation (needs focal points + CII + convergence)
      const hotspotEscalations = detectHotspotEscalations(events, intermediateResult);

      // Infrastructure cascade (needs events with infrastructure tags)
      const affectedNodeIds = findAffectedNodes(events);
      const cascades = affectedNodeIds.length > 0
        ? analyzeCascade(affectedNodeIds)
        : [];

      // Update intermediate with cascade results for cross-module
      intermediateResult.hotspotEscalations = hotspotEscalations;
      intermediateResult.cascades = cascades;

      // Signal aggregation (needs all other results)
      const aggregatedSignals = aggregateSignals(events, intermediateResult);

      // Cross-module composite risk (needs everything above)
      const compositeRisks = calculateCompositeRisk(events, intermediateResult);

      // Lightweight modules — every tick
      const velocity = calculateVelocity(events);
      const trendingKeywords = detectTrendingKeywords(events);
      updateBaselines(events);

      // ---------------------------------------------------------------
      // Phase D: Additional analysis modules
      // ---------------------------------------------------------------
      const breakingNews = detectBreakingNews(events);
      const sentimentScores = analyzeSentiment(events);
      const aisAnomalies = detectAISSpoofing(events);

      // ---------------------------------------------------------------
      // Assemble final result
      // ---------------------------------------------------------------
      const result: AnalysisResult = {
        focalPoints,
        surges,
        convergences,
        ciiScores,
        correlations,
        hotspotEscalations,
        cascades,
        compositeRisks,
        aggregatedSignals,
        velocity,
        trendingKeywords,
        breakingNews,
        sentimentScores,
        aisAnomalies,
        timestamp: new Date().toISOString(),
      };

      this.lastResult = result;

      // Persist for warm-start on next session (fire-and-forget)
      saveAnalysisResult(result as unknown as Record<string, unknown>).catch(() => {});

      // Emit combined result
      eventBus.emit('analysis:complete', result);

      // Emit individual events for downstream listeners
      for (const fp of focalPoints) {
        eventBus.emit('analysis:focal-point', fp);
      }
      for (const surge of surges) {
        eventBus.emit('analysis:surge', surge);
      }
      for (const conv of convergences) {
        eventBus.emit('analysis:convergence', conv);
      }
      for (const esc of hotspotEscalations) {
        eventBus.emit('analysis:hotspot', esc);
      }
      for (const cascade of cascades) {
        eventBus.emit('analysis:cascade', cascade);
      }
      for (const risk of compositeRisks) {
        eventBus.emit('analysis:risk', risk);
      }
      if (velocity) {
        eventBus.emit('analysis:velocity', velocity);
      }
      if (trendingKeywords.length > 0) {
        eventBus.emit('analysis:trending', trendingKeywords);
      }
      for (const signal of aggregatedSignals) {
        eventBus.emit('analysis:signal', signal);
      }
      for (const alert of breakingNews) {
        eventBus.emit('analysis:breaking', alert);
      }
      if (sentimentScores.length > 0) {
        eventBus.emit('analysis:sentiment', sentimentScores);
      }
      for (const anomaly of aisAnomalies) {
        eventBus.emit('analysis:ais-anomaly', anomaly);
      }

      // Log summary when there's something to report
      const hasFindings =
        focalPoints.length > 0 ||
        surges.length > 0 ||
        convergences.length > 0 ||
        correlations.length > 0 ||
        hotspotEscalations.length > 0 ||
        cascades.length > 0 ||
        compositeRisks.length > 0 ||
        breakingNews.length > 0 ||
        sentimentScores.length > 0 ||
        aisAnomalies.length > 0;

      if (hasFindings) {
        console.log(
          `[Sentinel] Analysis: ${focalPoints.length} focal points, ` +
            `${surges.length} surges, ${convergences.length} convergences, ` +
            `${correlations.length} correlations, ` +
            `${hotspotEscalations.length} hotspot escalations, ` +
            `${cascades.length} cascades, ` +
            `${compositeRisks.length} composite risks, ` +
            `${breakingNews.length} breaking alerts, ` +
            `${sentimentScores.length} sentiment scores, ` +
            `${aisAnomalies.length} AIS anomalies` +
            (ciiActive
              ? `, ${ciiScores.size} CII scores`
              : ', CII warming up'),
        );
      }
    } finally {
      this.running = false;
    }
  }
}

// Singleton instance
let engineInstance: AnalysisEngine | null = null;

/** Initialize and start the analysis engine (singleton). */
export function initAnalysisEngine(): AnalysisEngine {
  if (engineInstance) return engineInstance;
  engineInstance = new AnalysisEngine();
  engineInstance.start();
  return engineInstance;
}

/** Get the running analysis engine, or null if not yet initialized. */
export function getAnalysisEngine(): AnalysisEngine | null {
  return engineInstance;
}
